import React, { useState, useRef, useEffect } from "react";
import { Users, Loader2, Network, GitBranch, MapPin, Clock, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

// All four board types. Drives both the popover UI and the recommended
// default per connection mode.
const BOARD_TYPES = [
  { id: "ties",        label: "Ties",        icon: Network,   color: "#22d3ee",
    blurb: "Classic corkboard — pin & string." },
  { id: "family_tree", label: "Family Tree", icon: GitBranch, color: "#ffd60a",
    blurb: "Vertical lineage by generation." },
  { id: "map",         label: "Geographic",  icon: MapPin,    color: "#00ff88",
    blurb: "Real world map — pins by lat/lng." },
  { id: "timeline",    label: "Timeline",    icon: Clock,     color: "#a78bfa",
    blurb: "Horizontal time ribbon by date." },
];

// Map Connect-Dots `mode` → recommended `board_type` for the seeded board.
const MODE_TO_BOARD = {
  geographic: "map",
  timeline:   "timeline",
  bloodline:  "family_tree",
};

/**
 * "Open in The Ties" button — now a one-click action (defaults to the
 * recommended board type for the source artifact's mode) PLUS a chevron
 * dropdown so the user can pick a different board if they want.
 *
 * Props:
 *  - kind: "dossier" | "ai_dossier" | "connection"
 *  - ref:  artifact id / slug
 *  - sourceMode: optional Connect-Dots mode (geographic | timeline | bloodline | ...) — used to recommend the right board type
 *  - variant: "primary" | "ghost"
 *  - size: "sm" | "md"
 */
export default function OpenInTiesButton({
  kind,
  ref: artifactRef,
  sourceMode,
  variant = "primary",
  size = "md",
  testid = "open-in-ties",
  label,
}) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  // Recommended board type — defaults to "ties" unless the source artifact
  // is a connection in a specialized mode.
  const recommended = MODE_TO_BOARD[sourceMode] || "ties";
  const recommendedDef = BOARD_TYPES.find((b) => b.id === recommended) || BOARD_TYPES[0];
  const RecIcon = recommendedDef.icon;

  // Close popover when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const spawn = async (boardType) => {
    if (!user) {
      window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "signup" } }));
      return;
    }
    if (!kind || !artifactRef) {
      toast.error("Missing artifact reference");
      return;
    }
    setBusy(true);
    setOpen(false);
    try {
      const r = await api.post(
        `/warroom/from/${kind}/${encodeURIComponent(artifactRef)}`,
        { mode: "solo", board_type: boardType }
      );
      const def = BOARD_TYPES.find((b) => b.id === boardType) || BOARD_TYPES[0];
      toast.success(`${def.label} board seeded · ${r.data?.node_count || 0} nodes / ${r.data?.edge_count || 0} ties`);
      nav(`/warroom/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open board");
    } finally {
      setBusy(false);
    }
  };

  const base = "inline-flex items-center font-mono uppercase tracking-[0.2em] transition-colors disabled:opacity-50";
  const sizeCls = size === "sm" ? "px-3 py-1.5 text-[10px]" : "px-4 py-2 text-[11px]";
  const variantCls = variant === "primary"
    ? "bg-[#22d3ee] text-[#050505] hover:bg-white"
    : "border border-[#22d3ee]/40 text-[#22d3ee] hover:bg-[#22d3ee]/10";

  // Split button: main label uses recommended board; chevron opens picker.
  return (
    <span className="inline-flex relative" ref={popRef}>
      <button
        type="button"
        onClick={() => spawn(recommended)}
        disabled={busy}
        data-testid={testid}
        title={`Spawn a ${recommendedDef.label} board pre-seeded with this artifact`}
        className={`${base} ${sizeCls} ${variantCls} gap-2`}
      >
        {busy
          ? <Loader2 size={11} className="animate-spin" />
          : <RecIcon size={11} />}
        {busy ? "SEEDING…" : (label || `Send to ${recommendedDef.label}`)}
      </button>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        data-testid={`${testid}-picker`}
        aria-label="Pick a different board type"
        title="Pick a different board type"
        className={`${base} ${sizeCls} ${variantCls} border-l-0 px-1.5`}
      >
        <ChevronDown size={11} />
      </button>

      {open && (
        <div
          className="absolute z-40 right-0 top-full mt-1 w-64 border border-[#22d3ee]/40 bg-[#0a0a0a] shadow-xl"
          data-testid={`${testid}-popover`}
          role="menu"
        >
          <div className="px-3 py-2 border-b border-white/10 font-mono text-[9px] tracking-[0.3em] text-zinc-500">
            // SEND TO …
          </div>
          {BOARD_TYPES.map((b) => {
            const Icon = b.icon;
            const isRec = b.id === recommended;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => spawn(b.id)}
                data-testid={`${testid}-pick-${b.id}`}
                className="w-full text-left flex items-start gap-2 px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-b-0"
              >
                <Icon size={14} style={{ color: b.color }} className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: b.color }}>
                    {b.label}
                    {isRec && (
                      <span className="ml-2 text-zinc-500 normal-case tracking-normal">recommended</span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{b.blurb}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
